import { AlertTriangle, CheckCircle2, Info, OctagonAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export type CalloutTone = 'info' | 'success' | 'warning' | 'danger';

/**
 * Een melding die bij de inhoud hoort en niet verdwijnt.
 *
 * Het verschil met een toast: een toast is een bericht over wat er net gebeurde en is na een paar
 * seconden weg. Dit is een toestand van het scherm - "de import is mislukt", "dit bericht staat
 * vast" - en die moet blijven staan zolang ze waar is. De fout van 6 oktober was precies dat
 * onderscheid: een mislukte import gaf een toast die wegtikte, en daarna stond het scherm weer op
 * "bezig" zonder dat er iets bezig was.
 *
 * Nooit kleur alleen: elke toon heeft een eigen icoon. Wie kleuren niet onderscheidt, ziet anders
 * geen verschil tussen "klaar" en "mislukt".
 */
const TOON: Record<CalloutTone, { wrapper: string; icon: typeof Info; iconClass: string }> = {
  info: {
    wrapper: 'border-border bg-background text-text-primary',
    icon: Info,
    iconClass: 'text-brand-primary',
  },
  success: {
    wrapper: 'border-success/30 bg-success-tint text-text-primary',
    icon: CheckCircle2,
    iconClass: 'text-success',
  },
  warning: {
    wrapper: 'border-warning/30 bg-warning-tint text-text-primary',
    icon: AlertTriangle,
    iconClass: 'text-warning',
  },
  danger: {
    wrapper: 'border-danger/30 bg-danger-tint text-text-primary',
    icon: OctagonAlert,
    iconClass: 'text-danger',
  },
};

export interface CalloutProps {
  tone?: CalloutTone;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  /** Een haak voor de test; `data-testid` hoort niet in de klassen te verdwijnen. */
  'data-testid'?: string;
}

export function Callout({
  tone = 'info',
  title,
  children,
  action,
  className,
  'data-testid': testId,
}: CalloutProps) {
  const stijl = TOON[tone];
  const Icon = stijl.icon;

  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      data-testid={testId}
      className={cn(
        'flex items-start gap-3 rounded-lg border p-element text-content',
        stijl.wrapper,
        className,
      )}
    >
      <Icon aria-hidden className={cn('mt-0.5 h-5 w-5 shrink-0', stijl.iconClass)} />
      <div className="min-w-0 flex-1">
        {title ? <p className="font-heading font-semibold">{title}</p> : null}
        {children ? <div className={cn(title && 'mt-1')}>{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
